import React, { useEffect, useState } from 'react';
import { getCurrentUser } from '../../Authentication/auth';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// Custom Modal Component
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, loading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="mt-2 text-sm text-gray-600">{message}</p>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center">
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Deleting...
              </span>
            ) : (
              'Delete'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const PropertySettings = () => {
  const [property, setProperty] = useState(null);
  const [formData, setFormData] = useState({
    propertyname: '',
    city: '',
    name: '',
    location: '',
    address: '',
    phone: '',
    email: '',
    maxGuestsPerRoom: 2,
    checkInTime: '14:00',
    checkOutTime: '12:00',
    cancellationPolicy: 'Free cancellation up to 24 hours before check-in',
    petPolicy: 'Not allowed',
    smokingPolicy: 'Not allowed',
    extraBedPolicy: 'Available with additional charge',
    hasWifi: true,
    hasPool: false,
    hasGym: false,
    hasRestaurant: true,
    hasParking: true,
    hasSpa: false,
    // New fields from API
    gstnumber: '',
    pan_number: '',
    fssai_number: '',
    cin_number: '',
    state_code: '',
    bank_account_name: '',
    bank_account_no: '',
    bank_ifsc: '',
  });

  const [logoFile, setLogoFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [homepageFile, setHomepageFile] = useState(null);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [errors, setErrors] = useState({});
  const [isDeleting, setIsDeleting] = useState({
    logo: false,
    homepageImage: false,
    video: false,
    all: false,
  });

  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfig, setDeleteConfig] = useState({
    type: '',
    title: '',
    message: '',
    onConfirm: () => {},
  });

  useEffect(() => {
    fetchProperty();
  }, []);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateIndianPhone = (phone) => {
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(phone);
  };

  const validateField = (name, value) => {
    const newErrors = { ...errors };

    switch (name) {
      case 'email':
        if (value && !validateEmail(value)) {
          newErrors.email = 'Please enter a valid email address';
        } else {
          delete newErrors.email;
        }
        break;

      case 'phone':
        if (value) {
          if (!validateIndianPhone(value)) {
            newErrors.phone = 'Please enter a valid 10-digit mobile number';
          } else {
            delete newErrors.phone;
          }
        } else {
          delete newErrors.phone;
        }
        break;

      case 'propertyname':
        if (!value?.trim()) {
          newErrors.propertyname = 'Property name is required';
        } else {
          delete newErrors.propertyname;
        }
        break;

      case 'city':
        if (!value?.trim()) {
          newErrors.city = 'City is required';
        } else {
          delete newErrors.city;
        }
        break;

      case 'name':
        if (!value?.trim()) {
          newErrors.name = 'Hotel name is required';
        } else {
          delete newErrors.name;
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const fetchProperty = async () => {
    try {
      const currentUser = await getCurrentUser();
      if (!currentUser) {
        toast.error('Failed to fetch user data. Please log in again.');
        return;
      }
      const res = await fetch(`${BQ_BASE_URL}/bq/api/properties/${currentUser.property_id}`);
      const data = await res.json();
      setProperty(data);

      // Format the time fields properly
      const formattedData = {
        ...data,
        checkInTime: data.checkin_time
          ? data.checkin_time.split('T')[1]?.substring(0, 5) || '14:00'
          : '14:00',
        checkOutTime: data.checkout_time
          ? data.checkout_time.split('T')[1]?.substring(0, 5) || '12:00'
          : '12:00',
      };

      setFormData((prev) => ({ ...prev, ...formattedData }));
    } catch (err) {
      console.error('Failed to fetch property:', err);
      toast.error('Failed to fetch property details');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Only validate email and phone, all other fields are optional
    if (name === 'email' || name === 'phone') {
      validateField(name, newValue);
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    // Only validate email and phone, all other fields are optional
    if (name === 'email' || name === 'phone') {
      validateField(name, value);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage('');

    const requiredFields = ['propertyname', 'city', 'name', 'email', 'phone'];
    let hasErrors = false;
    const newErrors = {};

    requiredFields.forEach((field) => {
      if (!formData[field]?.trim()) {
        newErrors[field] = `${
          field === 'propertyname'
            ? 'Property name'
            : field === 'city'
              ? 'City'
              : field === 'name'
                ? 'Hotel name'
                : field
        } is required`;
        hasErrors = true;
      }
    });

    if (formData.email && !validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      hasErrors = true;
    }

    if (formData.phone && !validateIndianPhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid 10-digit mobile number';
      hasErrors = true;
    }

    if (hasErrors) {
      setErrors(newErrors);
      setIsSubmitting(false);
      toast.error('Please fix the validation errors before submitting.');
      return;
    }

    const form = new FormData();

    // List of all fields that should be sent to the API
    const allFields = [
      'propertyname',
      'city',
      'name',
      'location',
      'address',
      'phone',
      'email',
      'maxGuestsPerRoom',
      'checkInTime',
      'checkOutTime',
      'cancellationPolicy',
      'petPolicy',
      'smokingPolicy',
      'extraBedPolicy',
      'hasWifi',
      'hasPool',
      'hasGym',
      'hasRestaurant',
      'hasParking',
      'hasSpa',
      // New fields
      'gstnumber',
      'pan_number',
      'fssai_number',
      'cin_number',
      'state_code',
      'bank_account_name',
      'bank_account_no',
      'bank_ifsc',
    ];

    // Send all fields, using empty string for blank optional fields
    allFields.forEach((key) => {
      let value = formData[key];

      // Convert boolean values to strings
      if (typeof value === 'boolean') {
        value = value.toString();
      }

      // For optional fields, send empty string if blank
      if (value === null || value === undefined) {
        value = '';
      }

      form.append(key, value.toString());
    });

    // Add files if they exist
    if (logoFile) form.append('logo_file', logoFile);
    if (homepageFile) form.append('homepage_file', homepageFile);
    if (videoFile) form.append('video_file', videoFile);

    try {
      const currentUser = await getCurrentUser();
      if (!currentUser) {
        toast.error('Failed to fetch user data. Please log in again.');
        setIsSubmitting(false);
        return;
      }

      const res = await fetch(`${BQ_BASE_URL}/bq/api/properties/${currentUser.property_id}`, {
        method: 'PUT',
        body: form,
      });

      if (!res.ok) {
        const errorData = await res.text();
        console.error('API Error:', errorData);
        throw new Error('Failed to update property');
      }

      const updated = await res.json();

      setProperty(updated);
      setFormData((prev) => ({ ...prev, ...updated }));
      setLogoFile(null);
      setHomepageFile(null);
      setVideoFile(null);
      setMessage('✅ Property updated successfully!');
      toast.success('Property updated successfully!');
    } catch (err) {
      console.error(err);
      setMessage('❌ Failed to update property');
      toast.error('Failed to update property');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show delete confirmation modal
  const showDeleteConfirmation = (type, title, message, onConfirmCallback) => {
    setDeleteConfig({
      type,
      title,
      message,
      onConfirm: () => {
        setShowDeleteModal(false);
        onConfirmCallback();
      },
    });
    setShowDeleteModal(true);
  };

  // API call to delete media
  const deleteMedia = async (endpoint, mediaType) => {
    try {
      const currentUser = await getCurrentUser();
      if (!currentUser || !currentUser.property_id) {
        toast.error('Failed to fetch user data. Please log in again.');
        return;
      }

      setIsDeleting((prev) => ({ ...prev, [mediaType]: true }));

      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/properties/${currentUser.property_id}${endpoint}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to delete ${mediaType}`);
      }

      const result = await response.json();

      // Update local state
      setProperty((prev) => ({
        ...prev,
        logo_url: mediaType === 'logo' ? null : prev.logo_url,
        homepage_url: mediaType === 'homepage' ? null : prev.homepage_url,
        homepage_video_url: mediaType === 'homepage_video' ? null : prev.homepage_video_url,
      }));

      setFormData((prev) => ({
        ...prev,
        logo_url: mediaType === 'logo' ? null : prev.logo_url,
        homepage_url: mediaType === 'homepage' ? null : prev.homepage_url,
        homepage_video_url: mediaType === 'homepage_video' ? null : prev.homepage_video_url,
      }));

      toast.success(
        mediaType === 'logo'
          ? 'Logo deleted successfully!'
          : mediaType === 'homepage'
            ? 'Homepage image deleted successfully!'
            : 'Video deleted successfully!'
      );

      // Clear file state if it was selected but not uploaded
      if (mediaType === 'logo') setLogoFile(null);
      if (mediaType === 'homepage') setHomepageFile(null);
      if (mediaType === 'homepage_video') setVideoFile(null);

      return result;
    } catch (error) {
      console.error(`Error deleting ${mediaType}:`, error);
      toast.error(`Failed to delete ${mediaType}: ${error.message}`);
      return null;
    } finally {
      setIsDeleting((prev) => ({ ...prev, [mediaType]: false }));
    }
  };

  const handleDeleteLogo = () => {
    showDeleteConfirmation(
      'logo',
      'Delete Logo',
      'Are you sure you want to delete the logo? This action cannot be undone.',
      async () => {
        const toastId = toast.loading('Deleting logo...');
        try {
          const result = await deleteMedia('/logo', 'logo');
          if (result) {
            toast.update(toastId, {
              render: 'Logo deleted successfully!',
              type: 'success',
              isLoading: false,
              autoClose: 3000,
            });
          } else {
            toast.update(toastId, {
              render: 'Failed to delete logo',
              type: 'error',
              isLoading: false,
              autoClose: 3000,
            });
          }
        } catch (error) {
          toast.update(toastId, {
            render: 'Error deleting logo',
            type: 'error',
            isLoading: false,
            autoClose: 3000,
          });
        }
      }
    );
  };

  const handleDeleteHomepageImage = () => {
    showDeleteConfirmation(
      'homepageImage',
      'Delete Homepage Image',
      'Are you sure you want to delete the homepage image? This action cannot be undone.',
      async () => {
        const toastId = toast.loading('Deleting homepage image...');
        try {
          const result = await deleteMedia('/homepage-image', 'homepage');
          if (result) {
            toast.update(toastId, {
              render: 'Homepage image deleted successfully!',
              type: 'success',
              isLoading: false,
              autoClose: 3000,
            });
          } else {
            toast.update(toastId, {
              render: 'Failed to delete homepage image',
              type: 'error',
              isLoading: false,
              autoClose: 3000,
            });
          }
        } catch (error) {
          toast.update(toastId, {
            render: 'Error deleting homepage image',
            type: 'error',
            isLoading: false,
            autoClose: 3000,
          });
        }
      }
    );
  };

  const handleDeleteVideo = () => {
    showDeleteConfirmation(
      'video',
      'Delete Property Video',
      'Are you sure you want to delete the property video? This action cannot be undone.',
      async () => {
        const toastId = toast.loading('Deleting video...');
        try {
          const result = await deleteMedia('/homepage-video', 'homepage_video');
          if (result) {
            toast.update(toastId, {
              render: 'Video deleted successfully!',
              type: 'success',
              isLoading: false,
              autoClose: 3000,
            });
          } else {
            toast.update(toastId, {
              render: 'Failed to delete video',
              type: 'error',
              isLoading: false,
              autoClose: 3000,
            });
          }
        } catch (error) {
          toast.update(toastId, {
            render: 'Error deleting video',
            type: 'error',
            isLoading: false,
            autoClose: 3000,
          });
        }
      }
    );
  };

  const handleDeleteAllMedia = () => {
    showDeleteConfirmation(
      'all',
      'Delete All Media',
      'Are you sure you want to delete ALL media (logo, homepage image, and video)? This action cannot be undone.',
      async () => {
        const toastId = toast.loading('Deleting all media...');
        try {
          const currentUser = await getCurrentUser();
          if (!currentUser || !currentUser.property_id) {
            toast.error('Failed to fetch user data. Please log in again.');
            return;
          }

          setIsDeleting((prev) => ({ ...prev, all: true }));

          const response = await fetch(
            `${BQ_BASE_URL}/bq/api/properties/${currentUser.property_id}/all-media`,
            {
              method: 'DELETE',
              headers: {
                'Content-Type': 'application/json',
              },
            }
          );

          if (!response.ok) {
            throw new Error('Failed to delete all media');
          }

          const result = await response.json();

          setProperty((prev) => ({
            ...prev,
            logo_url: null,
            homepage_url: null,
            homepage_video_url: null,
          }));

          setFormData((prev) => ({
            ...prev,
            logo_url: null,
            homepage_url: null,
            homepage_video_url: null,
          }));

          setLogoFile(null);
          setHomepageFile(null);
          setVideoFile(null);

          toast.update(toastId, {
            render: 'All media deleted successfully!',
            type: 'success',
            isLoading: false,
            autoClose: 3000,
          });

          return result;
        } catch (error) {
          console.error('Error deleting all media:', error);
          toast.update(toastId, {
            render: 'Failed to delete all media',
            type: 'error',
            isLoading: false,
            autoClose: 3000,
          });
          return null;
        } finally {
          setIsDeleting((prev) => ({ ...prev, all: false }));
        }
      }
    );
  };

  const hasValidationErrors = () => {
    const requiredFields = ['propertyname', 'city', 'name', 'email', 'phone'];
    const hasEmptyFields = requiredFields.some((field) => !formData[field]?.trim());
    const hasFormatErrors = Object.keys(errors).length > 0;

    return hasEmptyFields || hasFormatErrors;
  };

  const closeModal = () => {
    setShowDeleteModal(false);
  };

  return (
    <div className="mx-auto px-4 py-8">
      <ToastContainer />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={closeModal}
        onConfirm={deleteConfig.onConfirm}
        title={deleteConfig.title}
        message={deleteConfig.message}
        loading={isDeleting[deleteConfig.type]}
      />

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Tabs */}
        <div className="flex border-b overflow-x-auto">
          <button
            onClick={() => setActiveTab('basic')}
            className={`px-6 py-3 font-medium whitespace-nowrap ${
              activeTab === 'basic'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-blue-500'
            }`}
          >
            Basic Information
          </button>
          <button
            onClick={() => setActiveTab('business')}
            className={`px-6 py-3 font-medium whitespace-nowrap ${
              activeTab === 'business'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-blue-500'
            }`}
          >
            Business Details
          </button>
          <button
            onClick={() => setActiveTab('banking')}
            className={`px-6 py-3 font-medium whitespace-nowrap ${
              activeTab === 'banking'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-blue-500'
            }`}
          >
            Banking Information
          </button>
          <button
            onClick={() => setActiveTab('media')}
            className={`px-6 py-3 font-medium whitespace-nowrap ${
              activeTab === 'media'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-blue-500'
            }`}
          >
            Media & Branding
          </button>
          <button
            onClick={() => setActiveTab('rules')}
            className={`px-6 py-3 font-medium whitespace-nowrap ${
              activeTab === 'rules'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-blue-500'
            }`}
          >
            Hotel Rules
          </button>
        </div>

        {/* Form Content */}
        <div className="p-6">
          {message && (
            <div
              className={`mb-6 p-4 rounded-lg ${
                message.includes('✅') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}
            >
              {message}
            </div>
          )}

          {property ? (
            <form onSubmit={handleUpdate}>
              {/* Basic Information Tab */}
              {activeTab === 'basic' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    {
                      name: 'propertyname',
                      label: 'Property Name *',
                      type: 'text',
                      required: true,
                    },
                    {
                      name: 'city',
                      label: 'City *',
                      type: 'text',
                      required: true,
                    },
                    {
                      name: 'name',
                      label: 'Hotel Name *',
                      type: 'text',
                      required: true,
                    },
                    { name: 'location', label: 'Location', type: 'text' },
                    { name: 'address', label: 'Address', type: 'text' },
                    {
                      name: 'phone',
                      label: 'Mobile Number *',
                      type: 'tel',
                      required: true,
                      placeholder: 'Enter your Mobile number',
                    },
                    {
                      name: 'email',
                      label: 'Email *',
                      type: 'email',
                      required: true,
                    },
                  ].map((field) => (
                    <div key={field.name} className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">
                        {field.label}
                      </label>
                      <input
                        type={field.type}
                        name={field.name}
                        value={formData[field.name] || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={field.placeholder}
                        className={`w-full px-4 py-2 border rounded-lg ${
                          errors[field.name] ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors[field.name] && (
                        <p className="text-red-500 text-xs mt-1">{errors[field.name]}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Business Details Tab */}
              {activeTab === 'business' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">GST Number</label>
                    <input
                      type="text"
                      name="gstnumber"
                      value={formData.gstnumber || ''}
                      onChange={handleChange}
                      placeholder="Enter GST number (optional)"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                    <p className="text-xs text-gray-400">Optional</p>
                  </div>

                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">PAN Number</label>
                    <input
                      type="text"
                      name="pan_number"
                      value={formData.pan_number || ''}
                      onChange={handleChange}
                      placeholder="Enter PAN number (optional)"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                    <p className="text-xs text-gray-400">Optional</p>
                  </div>

                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">FSSAI Number</label>
                    <input
                      type="text"
                      name="fssai_number"
                      value={formData.fssai_number || ''}
                      onChange={handleChange}
                      placeholder="Enter FSSAI number (optional)"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                    <p className="text-xs text-gray-400">Optional</p>
                  </div>

                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">CIN Number</label>
                    <input
                      type="text"
                      name="cin_number"
                      value={formData.cin_number || ''}
                      onChange={handleChange}
                      placeholder="Enter CIN number (optional)"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                    <p className="text-xs text-gray-400">Optional</p>
                  </div>

                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">State Code</label>
                    <input
                      type="text"
                      name="state_code"
                      value={formData.state_code || ''}
                      onChange={handleChange}
                      placeholder="Enter state code (optional)"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                    <p className="text-xs text-gray-400">Optional</p>
                  </div>
                </div>
              )}

              {/* Banking Information Tab */}
              {activeTab === 'banking' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Bank Account Name
                    </label>
                    <input
                      type="text"
                      name="bank_account_name"
                      value={formData.bank_account_name || ''}
                      onChange={handleChange}
                      placeholder="Enter account holder name (optional)"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                    <p className="text-xs text-gray-400">Optional</p>
                  </div>

                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Bank Account Number
                    </label>
                    <input
                      type="text"
                      name="bank_account_no"
                      value={formData.bank_account_no || ''}
                      onChange={handleChange}
                      placeholder="Enter account number (optional)"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                    <p className="text-xs text-gray-400">Optional</p>
                  </div>

                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">IFSC Code</label>
                    <input
                      type="text"
                      name="bank_ifsc"
                      value={formData.bank_ifsc || ''}
                      onChange={handleChange}
                      placeholder="Enter IFSC code (optional)"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                    <p className="text-xs text-gray-400">Optional</p>
                  </div>
                </div>
              )}

              {/* Hotel Rules Tab */}
              {activeTab === 'rules' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Max Guests Per Room
                    </label>
                    <select
                      name="maxGuestsPerRoom"
                      value={formData.maxGuestsPerRoom}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      {[1, 2, 3, 4, 5].map((num) => (
                        <option key={num} value={num}>
                          {num} {num === 1 ? 'guest' : 'guests'}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">Check-in Time</label>
                    <input
                      type="time"
                      name="checkInTime"
                      value={formData.checkInTime}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Check-out Time
                    </label>
                    <input
                      type="time"
                      name="checkOutTime"
                      value={formData.checkOutTime}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Cancellation Policy
                    </label>
                    <select
                      name="cancellationPolicy"
                      value={formData.cancellationPolicy}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option>Free cancellation up to 24 hours before check-in</option>
                      <option>Free cancellation up to 48 hours before check-in</option>
                      <option>Non-refundable</option>
                      <option>Custom policy</option>
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">Pet Policy</label>
                    <select
                      name="petPolicy"
                      value={formData.petPolicy}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option>Not allowed</option>
                      <option>Allowed with restrictions</option>
                      <option>Allowed with additional charge</option>
                      <option>Allowed (no charge)</option>
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Smoking Policy
                    </label>
                    <select
                      name="smokingPolicy"
                      value={formData.smokingPolicy}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option>Not allowed</option>
                      <option>Allowed in designated areas</option>
                      <option>Allowed in all areas</option>
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Extra Bed Policy
                    </label>
                    <select
                      name="extraBedPolicy"
                      value={formData.extraBedPolicy}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option>Not available</option>
                      <option>Available with additional charge</option>
                      <option>Available (no charge)</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Media & Branding Tab */}
              {activeTab === 'media' && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h1 className="font-semibold text-xl">Property Media Settings</h1>
                      <h1 className="font-semibold text-sm text-gray-400">
                        Manage your property's homepage visuals with ease—update featured videos,
                        images, and logos to keep your brand fresh and engaging.
                      </h1>
                    </div>
                    {(property.logo_url ||
                      property.homepage_url ||
                      property.homepage_video_url) && (
                      <button
                        type="button"
                        onClick={handleDeleteAllMedia}
                        className="px-4 py-2 bg-red-100 text-red-600 hover:bg-red-200 font-medium rounded-lg text-sm transition-colors"
                      >
                        Delete All Media
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Logo Upload */}
                    <div className="space-y-4">
                      <div className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700">
                          Logo Image
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => setLogoFile(e.target.files?.[0])}
                            className="flex-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition"
                          />
                          {(logoFile || property.logo_url) && (
                            <button
                              type="button"
                              onClick={handleDeleteLogo}
                              disabled={isDeleting.logo}
                              className={`px-3 py-2 text-sm rounded transition whitespace-nowrap ${
                                isDeleting.logo
                                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                  : 'bg-red-500 text-white hover:bg-red-600'
                              }`}
                            >
                              {isDeleting.logo ? (
                                <span className="flex items-center">
                                  <svg
                                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    ></circle>
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                  </svg>
                                  Deleting...
                                </span>
                              ) : (
                                'Delete Logo'
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-center">
                        {logoFile ? (
                          <div className="relative">
                            <img
                              src={URL.createObjectURL(logoFile)}
                              alt="Preview Logo"
                              className="h-32 rounded-lg shadow-md object-contain"
                            />
                            <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                              New
                            </div>
                          </div>
                        ) : property.logo_url ? (
                          <div className="relative">
                            <img
                              src={property.logo_url}
                              alt="Logo"
                              className="h-32 rounded-lg shadow-md object-contain"
                            />
                            <div className="absolute top-2 right-2 bg-gray-500 text-white text-xs px-2 py-1 rounded">
                              Current
                            </div>
                          </div>
                        ) : (
                          <div className="h-32 w-full border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                            <span className="text-gray-400">No logo uploaded</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Homepage Upload */}
                    <div className="space-y-4">
                      <div className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700">
                          Homepage Image
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => setHomepageFile(e.target.files?.[0])}
                            className="flex-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition"
                          />
                          {(homepageFile || property.homepage_url) && (
                            <button
                              type="button"
                              onClick={handleDeleteHomepageImage}
                              disabled={isDeleting.homepageImage}
                              className={`px-3 py-2 text-sm rounded transition whitespace-nowrap ${
                                isDeleting.homepageImage
                                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                  : 'bg-red-500 text-white hover:bg-red-600'
                              }`}
                            >
                              {isDeleting.homepageImage ? (
                                <span className="flex items-center">
                                  <svg
                                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    ></circle>
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                  </svg>
                                  Deleting...
                                </span>
                              ) : (
                                'Delete Image'
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-center">
                        {homepageFile ? (
                          <div className="relative w-full">
                            <img
                              src={URL.createObjectURL(homepageFile)}
                              alt="Preview Homepage"
                              className="h-48 w-full rounded-lg shadow-md object-cover"
                            />
                            <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                              New
                            </div>
                          </div>
                        ) : property.homepage_url ? (
                          <div className="relative w-full">
                            <img
                              src={property.homepage_url}
                              alt="Homepage"
                              className="h-48 w-full rounded-lg shadow-md object-cover"
                            />
                            <div className="absolute top-2 right-2 bg-gray-500 text-white text-xs px-2 py-1 rounded">
                              Current
                            </div>
                          </div>
                        ) : (
                          <div className="h-48 w-full border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                            <span className="text-gray-400">No homepage image uploaded</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Video Upload */}
                    <div className="space-y-4 md:col-span-2">
                      <div className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700">
                          Property Video
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="file"
                            accept="video/*"
                            onChange={(e) => setVideoFile(e.target.files?.[0])}
                            className="flex-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition"
                          />
                          {(videoFile || property.homepage_video_url) && (
                            <button
                              type="button"
                              onClick={handleDeleteVideo}
                              disabled={isDeleting.video}
                              className={`px-3 py-2 text-sm rounded transition whitespace-nowrap ${
                                isDeleting.video
                                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                  : 'bg-red-500 text-white hover:bg-red-600'
                              }`}
                            >
                              {isDeleting.video ? (
                                <span className="flex items-center">
                                  <svg
                                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    ></circle>
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                  </svg>
                                  Deleting...
                                </span>
                              ) : (
                                'Delete Video'
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-center">
                        {videoFile ? (
                          <div className="relative w-full max-w-2xl">
                            <video controls className="w-full rounded-lg shadow-md">
                              <source src={URL.createObjectURL(videoFile)} type="video/mp4" />
                              Your browser does not support the video tag.
                            </video>
                            <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                              New
                            </div>
                          </div>
                        ) : property.homepage_video_url ? (
                          <div className="relative w-full max-w-xl">
                            <video controls className="w-full rounded-lg shadow-md">
                              <source src={property.homepage_video_url} type="video/mp4" />
                              Your browser does not support the video tag.
                            </video>
                            <div className="absolute top-2 right-2 bg-gray-500 text-white text-xs px-2 py-1 rounded">
                              Current
                            </div>
                          </div>
                        ) : (
                          <div className="w-full max-w-xl border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center py-16">
                            <span className="text-gray-400">No video uploaded</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button (shown on all tabs) */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={isSubmitting || hasValidationErrors()}
                  className={`w-full md:w-auto px-6 py-3 bg-black text-white font-semibold rounded-lg shadow-md hover:text-gray-200 focus:outline-none ${
                    isSubmitting || hasValidationErrors() ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Saving...
                    </span>
                  ) : (
                    ' Save Changes'
                  )}
                </button>
              </div>
            </form>
          ) : (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PropertySettings;

// import React, { useEffect, useState } from "react";
// import { getCurrentUser } from "../../Authentication/auth";
// import { ToastContainer, toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// // Custom Modal Component
// const DeleteConfirmationModal = ({
//   isOpen,
//   onClose,
//   onConfirm,
//   title,
//   message,
//   loading,
// }) => {
//   if (!isOpen) return null;

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//       <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
//         <div className="mb-4">
//           <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
//           <p className="mt-2 text-sm text-gray-600">{message}</p>
//         </div>

//         <div className="flex justify-end space-x-3">
//           <button
//             onClick={onClose}
//             disabled={loading}
//             className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
//           >
//             Cancel
//           </button>
//           <button
//             onClick={onConfirm}
//             disabled={loading}
//             className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
//           >
//             {loading ? (
//               <span className="flex items-center">
//                 <svg
//                   className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
//                   fill="none"
//                   viewBox="0 0 24 24"
//                 >
//                   <circle
//                     className="opacity-25"
//                     cx="12"
//                     cy="12"
//                     r="10"
//                     stroke="currentColor"
//                     strokeWidth="4"
//                   ></circle>
//                   <path
//                     className="opacity-75"
//                     fill="currentColor"
//                     d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
//                   ></path>
//                 </svg>
//                 Deleting...
//               </span>
//             ) : (
//               "Delete"
//             )}
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// const PropertySettings = () => {
//   const [property, setProperty] = useState(null);
//   const [formData, setFormData] = useState({
//     propertyname: "",
//     city: "",
//     name: "",
//     location: "",
//     address: "",
//     phone: "",
//     email: "",
//     maxGuestsPerRoom: 2,
//     checkInTime: "14:00",
//     checkOutTime: "12:00",
//     cancellationPolicy: "Free cancellation up to 24 hours before check-in",
//     petPolicy: "Not allowed",
//     smokingPolicy: "Not allowed",
//     extraBedPolicy: "Available with additional charge",
//     hasWifi: true,
//     hasPool: false,
//     hasGym: false,
//     hasRestaurant: true,
//     hasParking: true,
//     hasSpa: false,
//   });

//   const [logoFile, setLogoFile] = useState(null);
//   const [videoFile, setVideoFile] = useState(null);
//   const [homepageFile, setHomepageFile] = useState(null);
//   const [message, setMessage] = useState("");
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [activeTab, setActiveTab] = useState("basic");
//   const [errors, setErrors] = useState({});
//   const [isDeleting, setIsDeleting] = useState({
//     logo: false,
//     homepageImage: false,
//     video: false,
//     all: false,
//   });

//   // Modal states
//   const [showDeleteModal, setShowDeleteModal] = useState(false);
//   const [deleteConfig, setDeleteConfig] = useState({
//     type: "",
//     title: "",
//     message: "",
//     onConfirm: () => {},
//   });

//   useEffect(() => {
//     fetchProperty();
//   }, []);

//   const validateEmail = (email) => {
//     const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//     return emailRegex.test(email);
//   };

//   const validateIndianPhone = (phone) => {
//     const phoneRegex = /^\d{10}$/;
//     return phoneRegex.test(phone);
//   };

//   const validateField = (name, value) => {
//     const newErrors = { ...errors };

//     switch (name) {
//       case "email":
//         if (value && !validateEmail(value)) {
//           newErrors.email = "Please enter a valid email address";
//         } else {
//           delete newErrors.email;
//         }
//         break;

//       case "phone":
//         if (value) {
//           if (!validateIndianPhone(value)) {
//             newErrors.phone = "Please enter a valid 10-digit mobile number";
//           } else {
//             delete newErrors.phone;
//           }
//         } else {
//           delete newErrors.phone;
//         }
//         break;

//       case "propertyname":
//         if (!value.trim()) {
//           newErrors.propertyname = "Property name is required";
//         } else {
//           delete newErrors.propertyname;
//         }
//         break;

//       case "city":
//         if (!value.trim()) {
//           newErrors.city = "City is required";
//         } else {
//           delete newErrors.city;
//         }
//         break;

//       case "name":
//         if (!value.trim()) {
//           newErrors.name = "Hotel name is required";
//         } else {
//           delete newErrors.name;
//         }
//         break;

//       default:
//         break;
//     }

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   const fetchProperty = async () => {
//     try {
//       const currentUser = await getCurrentUser();
//       if (!currentUser) {
//         toast.error("Failed to fetch user data. Please log in again.");
//         return;
//       }
//       const res = await fetch(
//         `${BQ_BASE_URL}/bq/api/properties/${currentUser.property_id}`,
//       );
//       const data = await res.json();
//       setProperty(data);
//       setFormData((prev) => ({ ...prev, ...data }));
//     } catch (err) {
//       console.error("Failed to fetch property:", err);
//       toast.error("Failed to fetch property details");
//     }
//   };

//   const handleChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     const newValue = type === "checkbox" ? checked : value;

//     setFormData((prev) => ({
//       ...prev,
//       [name]: newValue,
//     }));

//     if (name === "email" || name === "phone") {
//       validateField(name, newValue);
//     }
//   };

//   const handleBlur = (e) => {
//     const { name, value } = e.target;
//     validateField(name, value);
//   };

//   const handleUpdate = async (e) => {
//     e.preventDefault();
//     setIsSubmitting(true);
//     setMessage("");

//     const requiredFields = ["propertyname", "city", "name", "email", "phone"];
//     let hasErrors = false;
//     const newErrors = {};

//     requiredFields.forEach((field) => {
//       if (!formData[field]?.trim()) {
//         newErrors[field] = `${
//           field === "propertyname"
//             ? "Property name"
//             : field === "city"
//               ? "City"
//               : field === "name"
//                 ? "Hotel name"
//                 : field
//         } is required`;
//         hasErrors = true;
//       }
//     });

//     if (formData.email && !validateEmail(formData.email)) {
//       newErrors.email = "Please enter a valid email address";
//       hasErrors = true;
//     }

//     if (formData.phone && !validateIndianPhone(formData.phone)) {
//       newErrors.phone = "Please enter a valid 10-digit mobile number";
//       hasErrors = true;
//     }

//     if (hasErrors) {
//       setErrors(newErrors);
//       setIsSubmitting(false);
//       toast.error("Please fix the validation errors before submitting.");
//       return;
//     }

//     const form = new FormData();
//     Object.entries(formData).forEach(([key, value]) => {
//       if (value !== null && value !== undefined) {
//         form.append(key, value);
//       }
//     });

//     if (logoFile) form.append("logo_file", logoFile);
//     if (homepageFile) form.append("homepage_file", homepageFile);
//     if (videoFile) form.append("video_file", videoFile);

//     try {
//       const currentUser = await getCurrentUser();
//       if (!currentUser) {
//         toast.error("Failed to fetch user data. Please log in again.");
//         setIsSubmitting(false);
//         return;
//       }
//       const res = await fetch(
//         `${BQ_BASE_URL}/bq/api/properties/${currentUser.property_id}`,
//         {
//           method: "PUT",
//           body: form,
//         },
//       );

//       if (!res.ok) throw new Error("Failed to update property");
//       const updated = await res.json();

//       setProperty(updated);
//       setFormData((prev) => ({ ...prev, ...updated }));
//       setLogoFile(null);
//       setHomepageFile(null);
//       setVideoFile(null);
//       setMessage("✅ Property updated successfully!");
//       toast.success("Property updated successfully!");
//     } catch (err) {
//       console.error(err);
//       setMessage("❌ Failed to update property");
//       toast.error("Failed to update property");
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   // Show delete confirmation modal
//   const showDeleteConfirmation = (type, title, message, onConfirmCallback) => {
//     setDeleteConfig({
//       type,
//       title,
//       message,
//       onConfirm: () => {
//         setShowDeleteModal(false);
//         onConfirmCallback();
//       },
//     });
//     setShowDeleteModal(true);
//   };

//   // API call to delete media
//   const deleteMedia = async (endpoint, mediaType) => {
//     try {
//       const currentUser = await getCurrentUser();
//       if (!currentUser || !currentUser.property_id) {
//         toast.error("Failed to fetch user data. Please log in again.");
//         return;
//       }

//       setIsDeleting((prev) => ({ ...prev, [mediaType]: true }));

//       // FIX: Use the correct endpoint format: /properties/{property_id}/{endpoint}
//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/properties/${currentUser.property_id}${endpoint}`,
//         {
//           method: "DELETE",
//           headers: {
//             "Content-Type": "application/json",
//           },
//         },
//       );

//       if (!response.ok) {
//         throw new Error(`Failed to delete ${mediaType}`);
//       }

//       const result = await response.json();

//       // Update local state
//       setProperty((prev) => ({
//         ...prev,
//         logo_url: mediaType === "logo" ? null : prev.logo_url,
//         homepage_url: mediaType === "homepage" ? null : prev.homepage_url,
//         homepage_video_url:
//           mediaType === "homepage_video" ? null : prev.homepage_video_url,
//       }));

//       setFormData((prev) => ({
//         ...prev,
//         logo_url: mediaType === "logo" ? null : prev.logo_url,
//         homepage_url: mediaType === "homepage" ? null : prev.homepage_url,
//         homepage_video_url:
//           mediaType === "homepage_video" ? null : prev.homepage_video_url,
//       }));

//       toast.success(
//         mediaType === "logo"
//           ? "Logo deleted successfully!"
//           : mediaType === "homepage"
//             ? "Homepage image deleted successfully!"
//             : "Video deleted successfully!",
//       );

//       // Clear file state if it was selected but not uploaded
//       if (mediaType === "logo") setLogoFile(null);
//       if (mediaType === "homepage") setHomepageFile(null);
//       if (mediaType === "homepage_video") setVideoFile(null);

//       return result;
//     } catch (error) {
//       console.error(`Error deleting ${mediaType}:`, error);
//       toast.error(`Failed to delete ${mediaType}: ${error.message}`);
//       return null;
//     } finally {
//       setIsDeleting((prev) => ({ ...prev, [mediaType]: false }));
//     }
//   };

//   const handleDeleteLogo = () => {
//     showDeleteConfirmation(
//       "logo",
//       "Delete Logo",
//       "Are you sure you want to delete the logo? This action cannot be undone.",
//       async () => {
//         const toastId = toast.loading("Deleting logo...");

//         try {
//           // FIX: Just "/logo" instead of "/properties/logo"
//           const result = await deleteMedia("/logo", "logo");

//           if (result) {
//             toast.update(toastId, {
//               render: "Logo deleted successfully!",
//               type: "success",
//               isLoading: false,
//               autoClose: 3000,
//             });
//           } else {
//             toast.update(toastId, {
//               render: "Failed to delete logo",
//               type: "error",
//               isLoading: false,
//               autoClose: 3000,
//             });
//           }
//         } catch (error) {
//           toast.update(toastId, {
//             render: "Error deleting logo",
//             type: "error",
//             isLoading: false,
//             autoClose: 3000,
//           });
//         }
//       },
//     );
//   };

//   const handleDeleteHomepageImage = () => {
//     showDeleteConfirmation(
//       "homepageImage",
//       "Delete Homepage Image",
//       "Are you sure you want to delete the homepage image? This action cannot be undone.",
//       async () => {
//         const toastId = toast.loading("Deleting homepage image...");

//         try {
//           // FIX: Just "/homepage-image" instead of "/properties/homepage-image"
//           const result = await deleteMedia("/homepage-image", "homepage");

//           if (result) {
//             toast.update(toastId, {
//               render: "Homepage image deleted successfully!",
//               type: "success",
//               isLoading: false,
//               autoClose: 3000,
//             });
//           } else {
//             toast.update(toastId, {
//               render: "Failed to delete homepage image",
//               type: "error",
//               isLoading: false,
//               autoClose: 3000,
//             });
//           }
//         } catch (error) {
//           toast.update(toastId, {
//             render: "Error deleting homepage image",
//             type: "error",
//             isLoading: false,
//             autoClose: 3000,
//           });
//         }
//       },
//     );
//   };

//   const handleDeleteVideo = () => {
//     showDeleteConfirmation(
//       "video",
//       "Delete Property Video",
//       "Are you sure you want to delete the property video? This action cannot be undone.",
//       async () => {
//         const toastId = toast.loading("Deleting video...");

//         try {
//           // FIX: Just "/homepage-video" instead of "/properties/homepage-video"
//           const result = await deleteMedia("/homepage-video", "homepage_video");

//           if (result) {
//             toast.update(toastId, {
//               render: "Video deleted successfully!",
//               type: "success",
//               isLoading: false,
//               autoClose: 3000,
//             });
//           } else {
//             toast.update(toastId, {
//               render: "Failed to delete video",
//               type: "error",
//               isLoading: false,
//               autoClose: 3000,
//             });
//           }
//         } catch (error) {
//           toast.update(toastId, {
//             render: "Error deleting video",
//             type: "error",
//             isLoading: false,
//             autoClose: 3000,
//           });
//         }
//       },
//     );
//   };

//   // Also update the bulk delete function
//   const handleDeleteAllMedia = () => {
//     showDeleteConfirmation(
//       "all",
//       "Delete All Media",
//       "Are you sure you want to delete ALL media (logo, homepage image, and video)? This action cannot be undone.",
//       async () => {
//         const toastId = toast.loading("Deleting all media...");

//         try {
//           const currentUser = await getCurrentUser();
//           if (!currentUser || !currentUser.property_id) {
//             toast.error("Failed to fetch user data. Please log in again.");
//             return;
//           }

//           setIsDeleting((prev) => ({ ...prev, all: true }));

//           // FIX: Just "/all-media" instead of "/properties/all-media"
//           const response = await fetch(
//             `${BQ_BASE_URL}/bq/api/properties/${currentUser.property_id}/all-media`,
//             {
//               method: "DELETE",
//               headers: {
//                 "Content-Type": "application/json",
//               },
//             },
//           );

//           if (!response.ok) {
//             throw new Error("Failed to delete all media");
//           }

//           const result = await response.json();

//           // Update local state
//           setProperty((prev) => ({
//             ...prev,
//             logo_url: null,
//             homepage_url: null,
//             homepage_video_url: null,
//           }));

//           setFormData((prev) => ({
//             ...prev,
//             logo_url: null,
//             homepage_url: null,
//             homepage_video_url: null,
//           }));

//           setLogoFile(null);
//           setHomepageFile(null);
//           setVideoFile(null);

//           toast.update(toastId, {
//             render: "All media deleted successfully!",
//             type: "success",
//             isLoading: false,
//             autoClose: 3000,
//           });

//           return result;
//         } catch (error) {
//           console.error("Error deleting all media:", error);
//           toast.update(toastId, {
//             render: "Failed to delete all media",
//             type: "error",
//             isLoading: false,
//             autoClose: 3000,
//           });
//           return null;
//         } finally {
//           setIsDeleting((prev) => ({ ...prev, all: false }));
//         }
//       },
//     );
//   };

//   const hasValidationErrors = () => {
//     const requiredFields = ["propertyname", "city", "name", "email", "phone"];
//     const hasEmptyFields = requiredFields.some(
//       (field) => !formData[field]?.trim(),
//     );
//     const hasFormatErrors = Object.keys(errors).length > 0;

//     return hasEmptyFields || hasFormatErrors;
//   };

//   const closeModal = () => {
//     setShowDeleteModal(false);
//   };

//   return (
//     <div className="mx-auto px-4 py-8">
//       <ToastContainer />

//       {/* Delete Confirmation Modal */}
//       <DeleteConfirmationModal
//         isOpen={showDeleteModal}
//         onClose={closeModal}
//         onConfirm={deleteConfig.onConfirm}
//         title={deleteConfig.title}
//         message={deleteConfig.message}
//         loading={isDeleting[deleteConfig.type]}
//       />

//       <div className="bg-white rounded-xl shadow-lg overflow-hidden">
//         {/* Tabs */}
//         <div className="flex border-b">
//           <button
//             onClick={() => setActiveTab("basic")}
//             className={`px-6 py-3 font-medium ${
//               activeTab === "basic"
//                 ? "text-blue-600 border-b-2 border-blue-600"
//                 : "text-gray-600 hover:text-blue-500"
//             }`}
//           >
//             Basic Information
//           </button>

//           <button
//             onClick={() => setActiveTab("media")}
//             className={`px-6 py-3 font-medium ${
//               activeTab === "media"
//                 ? "text-blue-600 border-b-2 border-blue-600"
//                 : "text-gray-600 hover:text-blue-500"
//             }`}
//           >
//             Media & Branding
//           </button>
//           <button
//             onClick={() => setActiveTab("rules")}
//             className={`px-6 py-3 font-medium ${
//               activeTab === "rules"
//                 ? "text-blue-600 border-b-2 border-blue-600"
//                 : "text-gray-600 hover:text-blue-500"
//             }`}
//           >
//             Hotel Rules
//           </button>
//         </div>

//         {/* Form Content */}
//         <div className="p-6">
//           {message && (
//             <div
//               className={`mb-6 p-4 rounded-lg ${
//                 message.includes("✅")
//                   ? "bg-green-100 text-green-800"
//                   : "bg-red-100 text-red-800"
//               }`}
//             >
//               {message}
//             </div>
//           )}

//           {property ? (
//             <form onSubmit={handleUpdate}>
//               {/* Basic Information Tab */}
//               {activeTab === "basic" && (
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   {[
//                     {
//                       name: "propertyname",
//                       label: "Property Name *",
//                       type: "text",
//                       required: true,
//                     },
//                     {
//                       name: "city",
//                       label: "City *",
//                       type: "text",
//                       required: true,
//                     },
//                     {
//                       name: "name",
//                       label: "Hotel Name *",
//                       type: "text",
//                       required: true,
//                     },
//                     { name: "location", label: "Location", type: "text" },
//                     { name: "address", label: "Address", type: "text" },
//                     {
//                       name: "phone",
//                       label: "Mobile Number *",
//                       type: "tel",
//                       required: true,
//                       placeholder: "Enter your Mobile number",
//                     },
//                     {
//                       name: "email",
//                       label: "Email *",
//                       type: "email",
//                       required: true,
//                     },
//                   ].map((field) => (
//                     <div key={field.name} className="space-y-1">
//                       <label className="block text-sm font-medium text-gray-700">
//                         {field.label}
//                       </label>
//                       <input
//                         type={field.type}
//                         name={field.name}
//                         value={formData[field.name] || ""}
//                         onChange={handleChange}
//                         onBlur={handleBlur}
//                         placeholder={field.placeholder}
//                         className={`w-full px-4 py-2 border rounded-lg ${
//                           errors[field.name]
//                             ? "border-red-500"
//                             : "border-gray-300"
//                         }`}
//                       />
//                       {errors[field.name] && (
//                         <p className="text-red-500 text-xs mt-1">
//                           {errors[field.name]}
//                         </p>
//                       )}
//                     </div>
//                   ))}
//                 </div>
//               )}

//               {/* Hotel Rules Tab */}
//               {activeTab === "rules" && (
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   <div className="space-y-1">
//                     <label className="block text-sm font-medium text-gray-700">
//                       Max Guests Per Room
//                     </label>
//                     <select
//                       name="maxGuestsPerRoom"
//                       value={formData.maxGuestsPerRoom}
//                       onChange={handleChange}
//                       className="w-full px-4 py-2 border border-gray-300 rounded-lg"
//                     >
//                       {[1, 2, 3, 4, 5].map((num) => (
//                         <option key={num} value={num}>
//                           {num} {num === 1 ? "guest" : "guests"}
//                         </option>
//                       ))}
//                     </select>
//                   </div>

//                   <div className="space-y-1">
//                     <label className="block text-sm font-medium text-gray-700">
//                       Check-in Time
//                     </label>
//                     <input
//                       type="time"
//                       name="checkInTime"
//                       value={formData.checkInTime}
//                       onChange={handleChange}
//                       className="w-full px-4 py-2 border border-gray-300 rounded-lg "
//                     />
//                   </div>

//                   <div className="space-y-1">
//                     <label className="block text-sm font-medium text-gray-700">
//                       Check-out Time
//                     </label>
//                     <input
//                       type="time"
//                       name="checkOutTime"
//                       value={formData.checkOutTime}
//                       onChange={handleChange}
//                       className="w-full px-4 py-2 border border-gray-300 rounded-lg "
//                     />
//                   </div>

//                   <div className="space-y-1">
//                     <label className="block text-sm font-medium text-gray-700">
//                       Cancellation Policy
//                     </label>
//                     <select
//                       name="cancellationPolicy"
//                       value={formData.cancellationPolicy}
//                       onChange={handleChange}
//                       className="w-full px-4 py-2 border border-gray-300 rounded-lg "
//                     >
//                       <option>
//                         Free cancellation up to 24 hours before check-in
//                       </option>
//                       <option>
//                         Free cancellation up to 48 hours before check-in
//                       </option>
//                       <option>Non-refundable</option>
//                       <option>Custom policy</option>
//                     </select>
//                   </div>

//                   <div className="space-y-1">
//                     <label className="block text-sm font-medium text-gray-700">
//                       Pet Policy
//                     </label>
//                     <select
//                       name="petPolicy"
//                       value={formData.petPolicy}
//                       onChange={handleChange}
//                       className="w-full px-4 py-2 border border-gray-300 rounded-lg "
//                     >
//                       <option>Not allowed</option>
//                       <option>Allowed with restrictions</option>
//                       <option>Allowed with additional charge</option>
//                       <option>Allowed (no charge)</option>
//                     </select>
//                   </div>

//                   <div className="space-y-1">
//                     <label className="block text-sm font-medium text-gray-700">
//                       Smoking Policy
//                     </label>
//                     <select
//                       name="smokingPolicy"
//                       value={formData.smokingPolicy}
//                       onChange={handleChange}
//                       className="w-full px-4 py-2 border border-gray-300 rounded-lg "
//                     >
//                       <option>Not allowed</option>
//                       <option>Allowed in designated areas</option>
//                       <option>Allowed in all areas</option>
//                     </select>
//                   </div>

//                   <div className="space-y-1">
//                     <label className="block text-sm font-medium text-gray-700">
//                       Extra Bed Policy
//                     </label>
//                     <select
//                       name="extraBedPolicy"
//                       value={formData.extraBedPolicy}
//                       onChange={handleChange}
//                       className="w-full px-4 py-2 border border-gray-300 rounded-lg "
//                     >
//                       <option>Not available</option>
//                       <option>Available with additional charge</option>
//                       <option>Available (no charge)</option>
//                     </select>
//                   </div>
//                 </div>
//               )}

//               {/* Media & Branding Tab */}
//               {activeTab === "media" && (
//                 <div>
//                   <div className="flex justify-between items-center mb-4">
//                     <div>
//                       <h1 className="font-semibold text-xl">
//                         Property Media Settings
//                       </h1>
//                       <h1 className="font-semibold text-sm text-gray-400">
//                         Manage your property's homepage visuals with ease—update
//                         featured videos, images, and logos to keep your brand
//                         fresh and engaging.
//                       </h1>
//                     </div>
//                     {(property.logo_url ||
//                       property.homepage_url ||
//                       property.homepage_video_url) && (
//                       <button
//                         type="button"
//                         onClick={handleDeleteAllMedia}
//                         className="px-4 py-2 bg-red-100 text-red-600 hover:bg-red-200 font-medium rounded-lg text-sm transition-colors"
//                       >
//                         Delete All Media
//                       </button>
//                     )}
//                   </div>
//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                     {/* Logo Upload */}
//                     <div className="space-y-4">
//                       <div className="space-y-1">
//                         <label className="block text-sm font-medium text-gray-700">
//                           Logo Image
//                         </label>
//                         <div className="flex items-center gap-2">
//                           <input
//                             type="file"
//                             accept="image/*"
//                             onChange={(e) => setLogoFile(e.target.files?.[0])}
//                             className="flex-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition"
//                           />
//                           {(logoFile || property.logo_url) && (
//                             <button
//                               type="button"
//                               onClick={handleDeleteLogo}
//                               disabled={isDeleting.logo}
//                               className={`px-3 py-2 text-sm rounded transition whitespace-nowrap ${
//                                 isDeleting.logo
//                                   ? "bg-gray-300 text-gray-500 cursor-not-allowed"
//                                   : "bg-red-500 text-white hover:bg-red-600"
//                               }`}
//                             >
//                               {isDeleting.logo ? (
//                                 <span className="flex items-center">
//                                   <svg
//                                     className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
//                                     fill="none"
//                                     viewBox="0 0 24 24"
//                                   >
//                                     <circle
//                                       className="opacity-25"
//                                       cx="12"
//                                       cy="12"
//                                       r="10"
//                                       stroke="currentColor"
//                                       strokeWidth="4"
//                                     ></circle>
//                                     <path
//                                       className="opacity-75"
//                                       fill="currentColor"
//                                       d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
//                                     ></path>
//                                   </svg>
//                                   Deleting...
//                                 </span>
//                               ) : (
//                                 "Delete Logo"
//                               )}
//                             </button>
//                           )}
//                         </div>
//                       </div>
//                       <div className="flex justify-center">
//                         {logoFile ? (
//                           <div className="relative">
//                             <img
//                               src={URL.createObjectURL(logoFile)}
//                               alt="Preview Logo"
//                               className="h-32 rounded-lg shadow-md object-contain"
//                             />
//                             <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
//                               New
//                             </div>
//                           </div>
//                         ) : property.logo_url ? (
//                           <div className="relative">
//                             <img
//                               src={property.logo_url}
//                               alt="Logo"
//                               className="h-32 rounded-lg shadow-md object-contain"
//                             />
//                             <div className="absolute top-2 right-2 bg-gray-500 text-white text-xs px-2 py-1 rounded">
//                               Current
//                             </div>
//                           </div>
//                         ) : (
//                           <div className="h-32 w-full border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
//                             <span className="text-gray-400">
//                               No logo uploaded
//                             </span>
//                           </div>
//                         )}
//                       </div>
//                     </div>

//                     {/* Homepage Upload */}
//                     <div className="space-y-4">
//                       <div className="space-y-1">
//                         <label className="block text-sm font-medium text-gray-700">
//                           Homepage Image
//                         </label>
//                         <div className="flex items-center gap-2">
//                           <input
//                             type="file"
//                             accept="image/*"
//                             onChange={(e) =>
//                               setHomepageFile(e.target.files?.[0])
//                             }
//                             className="flex-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition"
//                           />
//                           {(homepageFile || property.homepage_url) && (
//                             <button
//                               type="button"
//                               onClick={handleDeleteHomepageImage}
//                               disabled={isDeleting.homepageImage}
//                               className={`px-3 py-2 text-sm rounded transition whitespace-nowrap ${
//                                 isDeleting.homepageImage
//                                   ? "bg-gray-300 text-gray-500 cursor-not-allowed"
//                                   : "bg-red-500 text-white hover:bg-red-600"
//                               }`}
//                             >
//                               {isDeleting.homepageImage ? (
//                                 <span className="flex items-center">
//                                   <svg
//                                     className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
//                                     fill="none"
//                                     viewBox="0 0 24 24"
//                                   >
//                                     <circle
//                                       className="opacity-25"
//                                       cx="12"
//                                       cy="12"
//                                       r="10"
//                                       stroke="currentColor"
//                                       strokeWidth="4"
//                                     ></circle>
//                                     <path
//                                       className="opacity-75"
//                                       fill="currentColor"
//                                       d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
//                                     ></path>
//                                   </svg>
//                                   Deleting...
//                                 </span>
//                               ) : (
//                                 "Delete Image"
//                               )}
//                             </button>
//                           )}
//                         </div>
//                       </div>
//                       <div className="flex justify-center">
//                         {homepageFile ? (
//                           <div className="relative w-full">
//                             <img
//                               src={URL.createObjectURL(homepageFile)}
//                               alt="Preview Homepage"
//                               className="h-48 w-full rounded-lg shadow-md object-cover"
//                             />
//                             <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
//                               New
//                             </div>
//                           </div>
//                         ) : property.homepage_url ? (
//                           <div className="relative w-full">
//                             <img
//                               src={property.homepage_url}
//                               alt="Homepage"
//                               className="h-48 w-full rounded-lg shadow-md object-cover"
//                             />
//                             <div className="absolute top-2 right-2 bg-gray-500 text-white text-xs px-2 py-1 rounded">
//                               Current
//                             </div>
//                           </div>
//                         ) : (
//                           <div className="h-48 w-full border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
//                             <span className="text-gray-400">
//                               No homepage image uploaded
//                             </span>
//                           </div>
//                         )}
//                       </div>
//                     </div>

//                     {/* Video Upload */}
//                     <div className="space-y-4 md:col-span-2">
//                       <div className="space-y-1">
//                         <label className="block text-sm font-medium text-gray-700">
//                           Property Video
//                         </label>
//                         <div className="flex items-center gap-2">
//                           <input
//                             type="file"
//                             accept="video/*"
//                             onChange={(e) => setVideoFile(e.target.files?.[0])}
//                             className="flex-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition"
//                           />
//                           {(videoFile || property.homepage_video_url) && (
//                             <button
//                               type="button"
//                               onClick={handleDeleteVideo}
//                               disabled={isDeleting.video}
//                               className={`px-3 py-2 text-sm rounded transition whitespace-nowrap ${
//                                 isDeleting.video
//                                   ? "bg-gray-300 text-gray-500 cursor-not-allowed"
//                                   : "bg-red-500 text-white hover:bg-red-600"
//                               }`}
//                             >
//                               {isDeleting.video ? (
//                                 <span className="flex items-center">
//                                   <svg
//                                     className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
//                                     fill="none"
//                                     viewBox="0 0 24 24"
//                                   >
//                                     <circle
//                                       className="opacity-25"
//                                       cx="12"
//                                       cy="12"
//                                       r="10"
//                                       stroke="currentColor"
//                                       strokeWidth="4"
//                                     ></circle>
//                                     <path
//                                       className="opacity-75"
//                                       fill="currentColor"
//                                       d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
//                                     ></path>
//                                   </svg>
//                                   Deleting...
//                                 </span>
//                               ) : (
//                                 "Delete Video"
//                               )}
//                             </button>
//                           )}
//                         </div>
//                       </div>
//                       <div className="flex justify-center">
//                         {videoFile ? (
//                           <div className="relative w-full max-w-2xl">
//                             <video
//                               controls
//                               className="w-full rounded-lg shadow-md"
//                             >
//                               <source
//                                 src={URL.createObjectURL(videoFile)}
//                                 type="video/mp4"
//                               />
//                               Your browser does not support the video tag.
//                             </video>
//                             <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
//                               New
//                             </div>
//                           </div>
//                         ) : property.homepage_video_url ? (
//                           <div className="relative w-full max-w-xl">
//                             <video
//                               controls
//                               className="w-full rounded-lg shadow-md"
//                             >
//                               <source
//                                 src={property.homepage_video_url}
//                                 type="video/mp4"
//                               />
//                               Your browser does not support the video tag.
//                             </video>
//                             <div className="absolute top-2 right-2 bg-gray-500 text-white text-xs px-2 py-1 rounded">
//                               Current
//                             </div>
//                           </div>
//                         ) : (
//                           <div className="w-full max-w-xl border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center py-16">
//                             <span className="text-gray-400">
//                               No video uploaded
//                             </span>
//                           </div>
//                         )}
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               )}

//               {/* Submit Button (shown on all tabs) */}
//               <div className="mt-8 pt-6 border-t border-gray-200">
//                 <button
//                   type="submit"
//                   disabled={isSubmitting || hasValidationErrors()}
//                   className={`w-full md:w-auto px-6 py-3 bg-black text-white font-semibold rounded-lg shadow-md hover:text-gray-200 focus:outline-none ${
//                     isSubmitting || hasValidationErrors()
//                       ? "opacity-70 cursor-not-allowed"
//                       : ""
//                   }`}
//                 >
//                   {isSubmitting ? (
//                     <span className="flex items-center justify-center">
//                       <svg
//                         className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
//                         xmlns="http://www.w3.org/2000/svg"
//                         fill="none"
//                         viewBox="0 0 24 24"
//                       >
//                         <circle
//                           className="opacity-25"
//                           cx="12"
//                           cy="12"
//                           r="10"
//                           stroke="currentColor"
//                           strokeWidth="4"
//                         ></circle>
//                         <path
//                           className="opacity-75"
//                           fill="currentColor"
//                           d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
//                         ></path>
//                       </svg>
//                       Saving...
//                     </span>
//                   ) : (
//                     " Save Changes"
//                   )}
//                 </button>
//               </div>
//             </form>
//           ) : (
//             <div className="flex justify-center items-center py-12">
//               <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default PropertySettings;
